import { __decorate } from 'tslib';
import * as i0 from '@angular/core';
import { Directive, HostBinding, Input, Component, NgModule, EventEmitter, Output, ChangeDetectionStrategy, ViewChildren, Injectable, ViewChild } from '@angular/core';
import * as i2 from '@angular/common';
import { CommonModule } from '@angular/common';
import * as i1 from '@ngx-translate/core';
import { TranslateModule } from '@ngx-translate/core';
import { CoerceBoolean } from '@slidebean/hydra/coercion';
import * as i3 from '@slidebean/kappa';
import { css, Styled, SetComponent, KappaModule, propsToCss, IconsModule, PopoverBehavior } from '@slidebean/kappa';
import * as i6 from '@slidebean/ui/emoji';
import { EmojiModule } from '@slidebean/ui/emoji';
import { OutsideZoneModule } from '@slidebean/ui/outside-zone';
import { Subject, BehaviorSubject, fromEvent, merge } from 'rxjs';
import { distinctUntilChanged, takeUntil, withLatestFrom, debounceTime, filter } from 'rxjs/operators';
import Quill, { Parchment } from 'quill';
import isEqual from 'fast-deep-equal';
import { findScrollContainer } from '@slidebean/ui/scroll';
import Embed from 'quill/blots/embed';
import Inline from 'quill/blots/inline';
import { AlignStyle } from 'quill/formats/align';
import Block from 'quill/blots/block';
import Link from 'quill/formats/link';
import ListItemBlot from 'quill/formats/list';

const EMPTY_RANGE = { index: 0, length: 0 };
const EMPTY_SELECTION = { range: EMPTY_RANGE, text: '' };

const $ToolbarItem = css({
    display: 'flex',
    h: '$full',
    w: '$full',
    border: '$none',
    cursor: 'pointer',
    background: '$panel',
    alignItems: 'center',
    justifyContent: 'center',
    minW: '$8',
    p: '$xsmall',
    '&:focus': { outline: 'none' },
    '&:hover': {
        background: '$primary-subtle',
    },
    '&:disabled': {
        cursor: 'default',
        pointerEvents: 'false',
        opacity: 0.4,
        color: '$foreground-subtle',
    },
    variants: {
        active: {
            true: {
                color: '$primary-solid',
            },
        },
        disabled: {
            true: {
                cursor: 'default',
                pointerEvents: 'none',
                opacity: 0.4,
                color: '$foreground-subtle',
            },
        },
    },
});
let ToolbarItemDirective = class ToolbarItemDirective {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.10", ngImport: i0, type: ToolbarItemDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.2.10", type: ToolbarItemDirective, selector: "button[sb-wysiwyg-toolbar-item]", inputs: { disabled: "disabled", active: "active" }, host: { properties: { "class": "this.class" } }, ngImport: i0 }); }
};
__decorate([
    CoerceBoolean()
], ToolbarItemDirective.prototype, "disabled", void 0);
__decorate([
    CoerceBoolean()
], ToolbarItemDirective.prototype, "active", void 0);
ToolbarItemDirective = __decorate([
    Styled($ToolbarItem)
], ToolbarItemDirective);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.10", ngImport: i0, type: ToolbarItemDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: 'button[sb-wysiwyg-toolbar-item]',
                }]
        }], propDecorators: { class: [{
                type: HostBinding,
                args: ['class']
            }], disabled: [{
                type: Input
            }], active: [{
                type: Input
            }] } });
const $ToolbarGroup = css({
    display: 'block',
    h: '$8',
    [`& > ${SetComponent}`]: {
        h: '$full',
    },
    variants: {
        type: {
            tuple: {
                'button[sb-wysiwyg-toolbar-item]:nth-of-type(1)': {
                    borderTopLeftRadius: '$xsmall',
                    borderBottomLeftRadius: '$xsmall',
                    border: '$borderWidths$regular solid $border',
                },
                'button[sb-wysiwyg-toolbar-item]:nth-of-type(2)': {
                    borderTopRightRadius: '$xsmall',
                    borderBottomRightRadius: '$xsmall',
                    borderTop: '$borderWidths$regular solid $border',
                    borderBottom: '$borderWidths$regular solid $border',
                    borderRight: '$borderWidths$regular solid $border',
                },
            },
            single: {
                borderRadius: '$xsmall',
                border: '$borderWidths$regular solid $border',
                'button[sb-wysiwyg-toolbar-item]': {
                    borderRadius: '$xsmall',
                },
            },
            group: {
                'button[sb-wysiwyg-toolbar-item]:nth-of-type(1)': {
                    borderTopLeftRadius: '$xsmall',
                    borderBottomLeftRadius: '$xsmall',
                    border: '$borderWidths$regular solid $border',
                },
                'button[sb-wysiwyg-toolbar-item]:nth-of-type(n + 1)': {
                    borderTop: '$borderWidths$regular solid $border',
                    borderBottom: '$borderWidths$regular solid $border',
                    borderRight: '$borderWidths$regular solid $border',
                },
                'button[sb-wysiwyg-toolbar-item]:nth-last-of-type(1)': {
                    borderTopRightRadius: '$xsmall',
                    borderBottomRightRadius: '$xsmall',
                    borderTop: '$borderWidths$regular solid $border',
                    borderBottom: '$borderWidths$regular solid $border',
                    borderRight: '$borderWidths$regular solid $border',
                },
            },
        },
    },
});
let ToolbarGroupComponent = class ToolbarGroupComponent {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.10", ngImport: i0, type: ToolbarGroupComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.10", type: ToolbarGroupComponent, selector: "sb-wysiwyg-toolbar-group", inputs: { type: "type" }, host: { properties: { "class": "this.class" } }, ngImport: i0, template: `<kp-set space="none" [grow]="true">
    <ng-content select="button[sb-wysiwyg-toolbar-item]"></ng-content>
  </kp-set>`, isInline: true, dependencies: [{ kind: "component", type: i3.SetComponent, selector: "kp-set", inputs: ["space", "alignmentX", "alignmentY", "collapseBelow", "wrap", "reverse", "grow"] }] }); }
};
ToolbarGroupComponent = __decorate([
    Styled($ToolbarGroup)
], ToolbarGroupComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.10", ngImport: i0, type: ToolbarGroupComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'sb-wysiwyg-toolbar-group',
                    template: `<kp-set space="none" [grow]="true">
    <ng-content select="button[sb-wysiwyg-toolbar-item]"></ng-content>
  </kp-set>`,
                }]
        }], propDecorators: { class: [{
                type: HostBinding,
                args: ['class']
            }], type: [{
                type: Input
            }] } });
const DIRECTIVES$4 = [ToolbarItemDirective, ToolbarGroupComponent];
class WysiwygToolbarItemModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.10", ngImport: i0, type: WysiwygToolbarItemModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.10", ngImport: i0, type: WysiwygToolbarItemModule, declarations: [ToolbarItemDirective, ToolbarGroupComponent], imports: [CommonModule, KappaModule], exports: [ToolbarItemDirective, ToolbarGroupComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.10", ngImport: i0, type: WysiwygToolbarItemModule, imports: [CommonModule, KappaModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.10", ngImport: i0, type: WysiwygToolbarItemModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule, KappaModule],
                    declarations: DIRECTIVES$4,
                    exports: DIRECTIVES$4,
                }]
        }] });

const $WysiwygColorSelectorItem = css({
    display: 'flex',
    borderRadius: '$round',
    alignItems: 'center',
    justifyContent: 'center',
    border: '$borderWidths$regular solid $border',
    size: '$8',
    '&:hover': {
        borderColor: '$primary-solid',
    },
});
let WysiwygColorSelectorItemComponent = class WysiwygColorSelectorItemComponent {
    get css() {
        return propsToCss({
            backgroundColor: this.color,
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.10", ngImport: i0, type: WysiwygColorSelectorItemComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.10", type: WysiwygColorSelectorItemComponent, selector: "sb-wysiwyg-color-selector-item", inputs: { color: "color" }, host: { properties: { "class": "this.class" } }, ngImport: i0, template: `<ng-content></ng-content>`, isInline: true }); }
};
WysiwygColorSelectorItemComponent = __decorate([
    Styled($WysiwygColorSelectorItem)
], WysiwygColorSelectorItemComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.10", ngImport: i0, type: WysiwygColorSelectorItemComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'sb-wysiwyg-color-selector-item',
                    template: `<ng-content></ng-content>`,
                }]
        }], propDecorators: { class: [{
                type: HostBinding,
                args: ['class']
            }], color: [{
                type: Input
            }] } });
const $WysiwygColorIndicator = css({
    display: 'block',
    '.sb-wysiwyg-color-indicator__bar': {
        borderRadius: '$xsmall',
        border: '$borderWidths$regular solid $border',
        h: '$1',
        w: '$6',
    },
});
let WysiwygColorIndicatorComponent = class WysiwygColorIndicatorComponent {
    get css() {
        return {
            '.sb-wysiwyg-color-indicator__bar': propsToCss({
                background: this.color,
            }),
        };
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.10", ngImport: i0, type: WysiwygColorIndicatorComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.10", type: WysiwygColorIndicatorComponent, selector: "sb-wysiwyg-color-indicator", inputs: { color: "color" }, host: { properties: { "class": "this.class" } }, ngImport: i0, template: `
    <kp-stack alignment="center">
      <ng-content></ng-content>

      <div class="sb-wysiwyg-color-indicator__bar"></div>
    </kp-stack>
  `, isInline: true, dependencies: [{ kind: "component", type: i3.StackComponent, selector: "kp-stack", inputs: ["space", "alignment", "grow"] }] }); }
};
WysiwygColorIndicatorComponent = __decorate([
    Styled($WysiwygColorIndicator)
], WysiwygColorIndicatorComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.10", ngImport: i0, type: WysiwygColorIndicatorComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'sb-wysiwyg-color-indicator',
                    template: `
    <kp-stack alignment="center">
      <ng-content></ng-content>

      <div class="sb-wysiwyg-color-indicator__bar"></div>
    </kp-stack>
  `,
                }]
        }], propDecorators: { class: [{
                type: HostBinding,
                args: ['class']
            }], color: [{
                type: Input
            }] } });
const NONE_KEY = 'none';
const $WysiwygColorsSelector = css({
    display: 'block',
    'hy-popper-host': {
        'kp-dropdown-panel': {
            p: '$small',
        },
    },
});
let WysiwygColorsSelectorComponent = class WysiwygColorsSelectorComponent {
    constructor() {
        this.colorSet = {};
        this.NONE_KEY = NONE_KEY;
        this.onSelectColor = new EventEmitter();
        this.onSelectBackground = new EventEmitter();
        this.colors = [];
    }
    ngOnChanges(changes) {
        if (changes['colorSet'] ||
            changes['activeColorKey'] ||
            changes['activeBackgroundKey']) {
            const { colorSet, activeColorKey, activeBackgroundKey } = this;
            // Extract unique colors
            const colorMap = {};
            const allKeys = Object.keys(colorSet);
            for (const key of allKeys) {
                if (!colorMap[colorSet[key]]) {
                    colorMap[colorSet[key]] = key;
                }
            }
            this.colors = Object.values(colorMap).map(key => ({
                key,
                value: colorSet[key],
            }));
            // Refresh active colors
            this.activeColor = this.colors.find(color => color.key === activeColorKey);
            this.activeBackground = this.colors.find(color => color.key === activeBackgroundKey);
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.10", ngImport: i0, type: WysiwygColorsSelectorComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.10", type: WysiwygColorsSelectorComponent, selector: "sb-wysiwyg-colors-selector", inputs: { colorSet: "colorSet", activeColorKey: "activeColorKey", activeBackgroundKey: "activeBackgroundKey" }, outputs: { onSelectColor: "onSelectColor", onSelectBackground: "onSelectBackground" }, host: { properties: { "class": "this.class" } }, usesOnChanges: true, ngImport: i0, template: `
    <sb-wysiwyg-toolbar-group type="tuple">
      <button
        sb-wysiwyg-toolbar-item
        [active]="!!activeColorKey"
        kp-dropdown-trigger
        kp-dropdown-trigger="click"
        kp-dropdown-placement="bottom"
        [kp-dropdown-panel]="colorContent"
      >
        <sb-wysiwyg-color-indicator [color]="activeColor?.value">
          <kp-font-icon size="small" />
        </sb-wysiwyg-color-indicator>

        <ng-template #colorContent>
          <kp-set space="small">
            <sb-wysiwyg-color-selector-item
              (click)="onSelectColor.emit(NONE_KEY)"
            >
              <kp-empty-set-icon size="small" />
            </sb-wysiwyg-color-selector-item>
            <sb-wysiwyg-color-selector-item
              *ngFor="let color of colors"
              [color]="color.value"
              (click)="onSelectColor.emit(color.key)"
            />
          </kp-set>
        </ng-template>
      </button>

      <button
        sb-wysiwyg-toolbar-item
        [active]="!!activeBackgroundKey"
        kp-dropdown-trigger
        kp-dropdown-trigger="click"
        kp-dropdown-placement="bottom"
        [kp-dropdown-panel]="backgroundContent"
      >
        <sb-wysiwyg-color-indicator [color]="activeBackground?.value">
          <kp-fill-drip-icon size="small" />
        </sb-wysiwyg-color-indicator>

        <ng-template #backgroundContent>
          <kp-set space="small">
            <sb-wysiwyg-color-selector-item
              (click)="onSelectBackground.emit(NONE_KEY)"
            >
              <kp-empty-set-icon size="small" />
            </sb-wysiwyg-color-selector-item>
            <sb-wysiwyg-color-selector-item
              *ngFor="let color of colors"
              [color]="color.value"
              (click)="onSelectBackground.emit(color.key)"
            />
          </kp-set>
        </ng-template>
      </button>
    </sb-wysiwyg-toolbar-group>
  `, isInline: true, dependencies: [{ kind: "directive", type: i2.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "component", type: i3.SetComponent, selector: "kp-set", inputs: ["space", "alignmentX", "alignmentY", "collapseBelow", "wrap", "reverse", "grow"] }, { kind: "directive", type: i3.DropdownTriggerSlot, selector: "[kp-dropdown-trigger]" }, { kind: "directive", type: i3.DropdownBehavior, selector: "[kp-dropdown-panel]", inputs: ["kp-dropdown-panel", "kp-dropdown-trigger", "kp-dropdown-placement", "kp-dropdown-keep-open-on-click", "fixed", "zIndex", "kp-dropdown-boundary"], outputs: ["shown", "hidden"], exportAs: ["dropdown-content"] }, { kind: "component", type: i3.FontIconComponent, selector: "kp-font-icon" }, { kind: "component", type: i3.FillDripIconComponent, selector: "kp-fill-drip-icon" }, { kind: "component", type: i3.EmptySetIconComponent, selector: "kp-empty-set-icon" }, { kind: "directive", type: ToolbarItemDirective, selector: "button[sb-wysiwyg-toolbar-item]", inputs: ["disabled", "active"] }, { kind: "component", type: ToolbarGroupComponent, selector: "sb-wysiwyg-toolbar-group", inputs: ["type"] }, { kind: "component", type: WysiwygColorSelectorItemComponent, selector: "sb-wysiwyg-color-selector-item", inputs: ["color"] }, { kind: "component", type: WysiwygColorIndicatorComponent, selector: "sb-wysiwyg-color-indicator", inputs: ["color"] }] }); }
};
WysiwygColorsSelectorComponent = __decorate([
    Styled($WysiwygColorsSelector)
], WysiwygColorsSelectorComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.10", ngImport: i0, type: WysiwygColorsSelectorComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'sb-wysiwyg-colors-selector',
                    template: `
    <sb-wysiwyg-toolbar-group type="tuple">
      <button
        sb-wysiwyg-toolbar-item
        [active]="!!activeColorKey"
        kp-dropdown-trigger
        kp-dropdown-trigger="click"
        kp-dropdown-placement="bottom"
        [kp-dropdown-panel]="colorContent"
      >
        <sb-wysiwyg-color-indicator [color]="activeColor?.value">
          <kp-font-icon size="small" />
        </sb-wysiwyg-color-indicator>

        <ng-template #colorContent>
          <kp-set space="small">
            <sb-wysiwyg-color-selector-item
              (click)="onSelectColor.emit(NONE_KEY)"
            >
              <kp-empty-set-icon size="small" />
            </sb-wysiwyg-color-selector-item>
            <sb-wysiwyg-color-selector-item
              *ngFor="let color of colors"
              [color]="color.value"
              (click)="onSelectColor.emit(color.key)"
            />
          </kp-set>
        </ng-template>
      </button>

      <button
        sb-wysiwyg-toolbar-item
        [active]="!!activeBackgroundKey"
        kp-dropdown-trigger
        kp-dropdown-trigger="click"
        kp-dropdown-placement="bottom"
        [kp-dropdown-panel]="backgroundContent"
      >
        <sb-wysiwyg-color-indicator [color]="activeBackground?.value">
          <kp-fill-drip-icon size="small" />
        </sb-wysiwyg-color-indicator>

        <ng-template #backgroundContent>
          <kp-set space="small">
            <sb-wysiwyg-color-selector-item
              (click)="onSelectBackground.emit(NONE_KEY)"
            >
              <kp-empty-set-icon size="small" />
            </sb-wysiwyg-color-selector-item>
            <sb-wysiwyg-color-selector-item
              *ngFor="let color of colors"
              [color]="color.value"
              (click)="onSelectBackground.emit(color.key)"
            />
          </kp-set>
        </ng-template>
      </button>
    </sb-wysiwyg-toolbar-group>
  `,
                }]
        }], propDecorators: { class: [{
                type: HostBinding,
                args: ['class']
            }], colorSet: [{
                type: Input
            }], activeColorKey: [{
                type: Input
            }], activeBackgroundKey: [{
                type: Input
            }], onSelectColor: [{
                type: Output
            }], onSelectBackground: [{
                type: Output
            }] } });
const DIRECTIVES$3 = [WysiwygColorsSelectorComponent];
class WysiwygColorsSelectorModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.10", ngImport: i0, type: WysiwygColorsSelectorModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.10", ngImport: i0, type: WysiwygColorsSelectorModule, declarations: [WysiwygColorsSelectorComponent, WysiwygColorSelectorItemComponent, WysiwygColorIndicatorComponent], imports: [CommonModule, KappaModule, IconsModule, WysiwygToolbarItemModule], exports: [WysiwygColorsSelectorComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.10", ngImport: i0, type: WysiwygColorsSelectorModule, imports: [CommonModule, KappaModule, IconsModule, WysiwygToolbarItemModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.10", ngImport: i0, type: WysiwygColorsSelectorModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule, KappaModule, IconsModule, WysiwygToolbarItemModule],
                    declarations: [
                        ...DIRECTIVES$3,
                        WysiwygColorSelectorItemComponent,
                        WysiwygColorIndicatorComponent,
                    ],
                    exports: DIRECTIVES$3,
                }]
        }] });

const $Toolbar = css({
    display: 'block',
    position: 'absolute',
    left: '-1px',
    boxShadow: ' $small',
    bottom: '100%',
    transform: 'translateY(0.5rem)',
    opacity: '0',
    pointerEvents: 'none',
    backgroundColor: '$panel',
    borderRadius: '$xsmall',
    border: '$borderWidths$regular solid $border',
    p: '$xsmall',
    '.sb-wysiwyg-toolbar__textSizes': {
        minW: '$32',
    },
    '.sb-wysiwyg-toolbar___link-popover': {
        p: '$small',
        '.sb-wysiwyg-toolbar___link-input': {
            w: '$72',
        },
    },
    '.sb-wysiwyg-toolbar__emoji hy-popper-host': {
        zIndex: '$tooltip',
        'kp-popover': {
            p: '$none',
        },
    },
    '.sb-wysiwyg-toolbar__chevron': {
        fontSize: '0.5rem',
    },
    variants: {
        show: {
            true: {
                transition: 'transform 100ms, opacity 100ms',
                transform: 'translateY(-5px)',
                opacity: '1',
                pointerEvents: 'auto',
                zIndex: '1',
            },
        },
    },
});
let WysiwygToolbarComponent = class WysiwygToolbarComponent {
    constructor(element, translate) {
        this.element = element;
        this.translate = translate;
        this.format = {};
        this.selection = EMPTY_SELECTION;
        this.onFormatSelected = new EventEmitter();
        this.onClearFormats = new EventEmitter();
        this.onEmojiSelected = new EventEmitter();
    }
    onTypeLink(text) {
        if (text === this.linkUrl)
            return;
        this.linkUrl = text;
    }
    getTextSizeLabel(option) {
        const label = this.translate.instant(option.label);
        return !!option.unit ? `${label}/${option.unit}` : label;
    }
    selectColor(key) {
        this.onFormatSelected.emit({
            label: '',
            name: 'color',
            value: key === NONE_KEY ? false : key,
        });
    }
    selectBackground(key) {
        this.onFormatSelected.emit({
            label: '',
            name: 'background',
            value: key === NONE_KEY ? false : key,
        });
    }
    hidePopovers() {
        this.popovers.forEach(p => p.hide());
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.10", ngImport: i0, type: WysiwygToolbarComponent, deps: [{ token: i0.ElementRef }, { token: i1.TranslateService }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.10", type: WysiwygToolbarComponent, selector: "sb-wysiwyg-toolbar", inputs: { show: "show", textSizes: "textSizes", colorSet: "colorSet", alignments: "alignments", styles: "styles", lists: "lists", format: "format", selection: "selection" }, outputs: { onFormatSelected: "onFormatSelected", onClearFormats: "onClearFormats", onEmojiSelected: "onEmojiSelected" }, host: { properties: { "class": "this.class" } }, viewQueries: [{ propertyName: "popovers", predicate: PopoverBehavior, descendants: true }], ngImport: i0, template: `
    <kp-set space="xsmall" [wrap]="true">
      <sb-wysiwyg-toolbar-group type="single" *ngIf="textSizes?.length">
        <button
          sb-wysiwyg-toolbar-item
          kp-dropdown-trigger
          kp-dropdown-trigger="click"
          kp-dropdown-placement="bottom"
          [kp-dropdown-panel]="textHeightContent"
        >
          <kp-set space="small" alignmentY="center">
            <kp-text-height-icon size="small" />
            <kp-chevron-down-icon
              size="small"
              class="sb-wysiwyg-toolbar__chevron"
            />
          </kp-set>

          <ng-template #textHeightContent>
            <kp-menu class="sb-wysiwyg-toolbar__textSizes">
              <kp-menu-item
                *ngFor="let option of textSizes"
                [kp-menu-active]="
                  format[option.name] === option.value ||
                  (!format[option.name] && !option.value)
                "
                [attr.kp-menu-active]="
                  format[option.name] === option.value ||
                  (!format[option.name] && !option.value)
                "
                (click.outside-zone)="onFormatSelected.emit(option)"
              >
                <kp-text size="small">{{ getTextSizeLabel(option) }}</kp-text>
              </kp-menu-item>
            </kp-menu>
          </ng-template>
        </button>
      </sb-wysiwyg-toolbar-group>

      <sb-wysiwyg-toolbar-group type="single" *ngIf="alignments?.length">
        <button
          sb-wysiwyg-toolbar-item
          kp-dropdown-trigger
          kp-dropdown-trigger="click"
          kp-dropdown-placement="bottom"
          [kp-dropdown-panel]="alignmentsContent"
        >
          <kp-set space="small" alignmentY="center">
            <kp-align-left-icon
              *ngIf="format['align'] === 'left'"
              size="small"
            />
            <kp-align-center-icon
              *ngIf="format['align'] === 'center'"
              size="small"
            />
            <kp-align-right-icon
              *ngIf="format['align'] === 'right'"
              size="small"
            />
            <kp-chevron-down-icon
              size="small"
              class="sb-wysiwyg-toolbar__chevron"
            />
          </kp-set>

          <ng-template #alignmentsContent>
            <kp-menu>
              <kp-menu-item
                *ngFor="let option of alignments"
                [kp-menu-active]="format[option.name] === option.value"
                [attr.kp-menu-active]="format[option.name] === option.value"
                (click.outside-zone)="onFormatSelected.emit(option)"
              >
                <kp-align-left-icon *ngIf="option.value === 'left'" />
                <kp-align-center-icon *ngIf="option.value === 'center'" />
                <kp-align-right-icon *ngIf="option.value === 'right'" />
              </kp-menu-item>
            </kp-menu>
          </ng-template>
        </button>
      </sb-wysiwyg-toolbar-group>

      <sb-wysiwyg-colors-selector
        *ngIf="!!colorSet"
        [colorSet]="colorSet"
        [activeColorKey]="format['color']"
        [activeBackgroundKey]="format['background']"
        (onSelectColor)="selectColor($event)"
        (onSelectBackground)="selectBackground($event)"
      />

      <sb-wysiwyg-toolbar-group type="group">
        <button
          sb-wysiwyg-toolbar-item
          *ngFor="let option of styles"
          [active]="
            option.name === 'script'
              ? format[option.name] === option.value
              : !!format[option.name]
          "
          (click.outside-zone)="
            onFormatSelected.emit({
              label: option.label,
              name: option.name,
              value:
                option.name === 'script' ? option.value : !format[option.name],
            })
          "
        >
          <kp-bold-icon *ngIf="option.name === 'bold'" size="small" />
          <kp-italic-icon *ngIf="option.name === 'italic'" size="small" />
          <kp-underline-icon *ngIf="option.name === 'underline'" size="small" />
          <kp-strikethrough-icon
            *ngIf="option.name === 'strike'"
            size="small"
          />
          <kp-subscript-icon
            *ngIf="option.name === 'script' && option.value === 'sub'"
            size="small"
          />
          <kp-superscript-icon
            *ngIf="option.name === 'script' && option.value === 'super'"
            size="small"
          />
        </button>
      </sb-wysiwyg-toolbar-group>

      <!--
        Rendering the emoji picker is a performance bottleneck in places where multiple wysiwy components are rendered.
        Render it only when the toolbar is visible.
      -->
      <sb-wysiwyg-toolbar-group
        *ngIf="show"
        type="single"
        class="sb-wysiwyg-toolbar__emoji"
      >
        <button
          sb-wysiwyg-toolbar-item
          [kp-popover]="emojiContent"
          kp-popover-trigger="click"
          kp-popover-placement="bottom"
        >
          <kp-face-laugh-wink-icon size="small" />

          <ng-template #emojiContent>
            <sb-emoji-picker
              (onEmojiPicked)="onEmojiSelected.emit($event); hidePopovers()"
            />
          </ng-template>
        </button>
      </sb-wysiwyg-toolbar-group>

      <sb-wysiwyg-toolbar-group type="tuple">
        <button
          sb-wysiwyg-toolbar-item
          *ngFor="let option of lists"
          (click.outside-zone)="onFormatSelected.emit(option)"
        >
          <kp-list-ul-icon *ngIf="option.value === 'bullet'" size="small" />
          <kp-list-ol-icon *ngIf="option.value === 'ordered'" size="small" />
        </button>
      </sb-wysiwyg-toolbar-group>

      <sb-wysiwyg-toolbar-group type="tuple">
        <button
          sb-wysiwyg-toolbar-item
          [disabled]="!format['link'] && selection?.text?.length == 0"
          [kp-popover]="linkContent"
          kp-popover-trigger="click"
          kp-popover-placement="bottom"
        >
          <kp-link-icon size="small" />

          <ng-template #linkContent>
            <div class="sb-wysiwyg-toolbar___link-popover">
              <kp-input
                class="sb-wysiwyg-toolbar___link-input"
                type="text"
                size="small"
                placeholder="http://"
                tabindex="-1"
                (changed)="onTypeLink($event)"
              >
                <kp-set kp-input-suffix>
                  <button
                    type="submit"
                    kp-button
                    tone="primary"
                    size="small"
                    (click)="
                      onFormatSelected.emit({
                        label: 'Link',
                        name: 'link',
                        value: linkUrl,
                      });
                      hidePopovers()
                    "
                  >
                    <kp-link-icon />
                  </button>
                </kp-set>
              </kp-input>
            </div>
          </ng-template>
        </button>

        <button
          sb-wysiwyg-toolbar-item
          [disabled]="!format['link']"
          (click.outside-zone)="
            onFormatSelected.emit({
              label: 'Link',
              name: 'link',
              value: false,
            })
          "
        >
          <kp-unlink-icon size="small" />
        </button>
      </sb-wysiwyg-toolbar-group>

      <sb-wysiwyg-toolbar-group type="single">
        <button
          sb-wysiwyg-toolbar-item
          [disabled]="selection?.text?.length == 0"
          (click.outside-zone)="onClearFormats.emit()"
        >
          <kp-text-slash-icon size="small" />
        </button>
      </sb-wysiwyg-toolbar-group>
    </kp-set>
  `, isInline: true, dependencies: [{ kind: "directive", type: i2.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: i3.TextComponent, selector: "kp-text, span[kp-text], a[kp-text]", inputs: ["tone", "weight", "size", "contrast"] }, { kind: "directive", type: i3.TextBackgroundBehavior, selector: "kp-text, span[kp-text], a[kp-text], kp-text-block, div[kp-text-block], p[kp-text-block], a[kp-text-block]" }, { kind: "component", type: i3.SetComponent, selector: "kp-set", inputs: ["space", "alignmentX", "alignmentY", "collapseBelow", "wrap", "reverse", "grow"] }, { kind: "component", type: i3.MenuComponent, selector: "kp-menu, [kp-menu]", inputs: ["space"] }, { kind: "directive", type: i3.MenuActiveDirective, selector: "kp-menu-item[kp-menu-active], [kp-menu-item][kp-menu-active]", inputs: ["kp-menu-active", "tone"] }, { kind: "component", type: i3.MenuItemComponent, selector: "kp-menu-item, [kp-menu-item]", inputs: ["disabled", "tone", "space"] }, { kind: "component", type: i3.InputComponent, selector: "kp-input", inputs: ["size", "tone", "type"] }, { kind: "directive", type: i3.InputBehavior, selector: "kp-input" }, { kind: "directive", type: i3.InputSuffixSlot, selector: "[kp-input-suffix]" }, { kind: "directive", type: i3.ButtonDirective, selector: "button[kp-button], a[kp-button]", inputs: ["tone", "size", "variant", "wrap", "grow"] }, { kind: "directive", type: i3.PopoverDirective, selector: "[kp-popover]", exportAs: ["popover"] }, { kind: "directive", type: i3.PopoverBehavior, selector: "[kp-popover]", inputs: ["kp-popover", "kp-popover-trigger", "kp-popover-placement"], exportAs: ["popover"] }, { kind: "directive", type: i3.DropdownTriggerSlot, selector: "[kp-dropdown-trigger]" }, { kind: "directive", type: i3.DropdownBehavior, selector: "[kp-dropdown-panel]", inputs: ["kp-dropdown-panel", "kp-dropdown-trigger", "kp-dropdown-placement", "kp-dropdown-keep-open-on-click", "fixed", "zIndex", "kp-dropdown-boundary"], outputs: ["shown", "hidden"], exportAs: ["dropdown-content"] }, { kind: "component", type: i3.ChevronDownIconComponent, selector: "kp-chevron-down-icon" }, { kind: "component", type: i3.LinkIconComponent, selector: "kp-link-icon" }, { kind: "component", type: i3.TextHeightIconComponent, selector: "kp-text-height-icon" }, { kind: "component", type: i3.AlignLeftIconComponent, selector: "kp-align-left-icon" }, { kind: "component", type: i3.AlignCenterIconComponent, selector: "kp-align-center-icon" }, { kind: "component", type: i3.AlignRightIconComponent, selector: "kp-align-right-icon" }, { kind: "component", type: i3.BoldIconComponent, selector: "kp-bold-icon" }, { kind: "component", type: i3.ItalicIconComponent, selector: "kp-italic-icon" }, { kind: "component", type: i3.UnderlineIconComponent, selector: "kp-underline-icon" }, { kind: "component", type: i3.StrikethroughIconComponent, selector: "kp-strikethrough-icon" }, { kind: "component", type: i3.SubscriptIconComponent, selector: "kp-subscript-icon" }, { kind: "component", type: i3.SuperscriptIconComponent, selector: "kp-superscript-icon" }, { kind: "component", type: i3.ListUlIconComponent, selector: "kp-list-ul-icon" }, { kind: "component", type: i3.ListOlIconComponent, selector: "kp-list-ol-icon" }, { kind: "component", type: i3.UnlinkIconComponent, selector: "kp-unlink-icon" }, { kind: "component", type: i3.TextSlashIconComponent, selector: "kp-text-slash-icon" }, { kind: "component", type: i3.FaceLaughWinkIconComponent, selector: "kp-face-laugh-wink-icon" }, { kind: "directive", type: ToolbarItemDirective, selector: "button[sb-wysiwyg-toolbar-item]", inputs: ["disabled", "active"] }, { kind: "component", type: ToolbarGroupComponent, selector: "sb-wysiwyg-toolbar-group", inputs: ["type"] }, { kind: "component", type: WysiwygColorsSelectorComponent, selector: "sb-wysiwyg-colors-selector", inputs: ["colorSet", "activeColorKey", "activeBackgroundKey"], outputs: ["onSelectColor", "onSelectBackground"] }, { kind: "component", type: i6.EmojiPickerComponent, selector: "sb-emoji-picker", inputs: ["color"], outputs: ["onEmojiPicked"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
};
__decorate([
    CoerceBoolean()
], WysiwygToolbarComponent.prototype, "show", void 0);
WysiwygToolbarComponent = __decorate([
    Styled($Toolbar)
], WysiwygToolbarComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.10", ngImport: i0, type: WysiwygToolbarComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'sb-wysiwyg-toolbar',
                    template: `
    <kp-set space="xsmall" [wrap]="true">
      <sb-wysiwyg-toolbar-group type="single" *ngIf="textSizes?.length">
        <button
          sb-wysiwyg-toolbar-item
          kp-dropdown-trigger
          kp-dropdown-trigger="click"
          kp-dropdown-placement="bottom"
          [kp-dropdown-panel]="textHeightContent"
        >
          <kp-set space="small" alignmentY="center">
            <kp-text-height-icon size="small" />
            <kp-chevron-down-icon
              size="small"
              class="sb-wysiwyg-toolbar__chevron"
            />
          </kp-set>

          <ng-template #textHeightContent>
            <kp-menu class="sb-wysiwyg-toolbar__textSizes">
              <kp-menu-item
                *ngFor="let option of textSizes"
                [kp-menu-active]="
                  format[option.name] === option.value ||
                  (!format[option.name] && !option.value)
                "
                [attr.kp-menu-active]="
                  format[option.name] === option.value ||
                  (!format[option.name] && !option.value)
                "
                (click.outside-zone)="onFormatSelected.emit(option)"
              >
                <kp-text size="small">{{ getTextSizeLabel(option) }}</kp-text>
              </kp-menu-item>
            </kp-menu>
          </ng-template>
        </button>
      </sb-wysiwyg-toolbar-group>

      <sb-wysiwyg-toolbar-group type="single" *ngIf="alignments?.length">
        <button
          sb-wysiwyg-toolbar-item
          kp-dropdown-trigger
          kp-dropdown-trigger="click"
          kp-dropdown-placement="bottom"
          [kp-dropdown-panel]="alignmentsContent"
        >
          <kp-set space="small" alignmentY="center">
            <kp-align-left-icon
              *ngIf="format['align'] === 'left'"
              size="small"
            />
            <kp-align-center-icon
              *ngIf="format['align'] === 'center'"
              size="small"
            />
            <kp-align-right-icon
              *ngIf="format['align'] === 'right'"
              size="small"
            />
            <kp-chevron-down-icon
              size="small"
              class="sb-wysiwyg-toolbar__chevron"
            />
          </kp-set>

          <ng-template #alignmentsContent>
            <kp-menu>
              <kp-menu-item
                *ngFor="let option of alignments"
                [kp-menu-active]="format[option.name] === option.value"
                [attr.kp-menu-active]="format[option.name] === option.value"
                (click.outside-zone)="onFormatSelected.emit(option)"
              >
                <kp-align-left-icon *ngIf="option.value === 'left'" />
                <kp-align-center-icon *ngIf="option.value === 'center'" />
                <kp-align-right-icon *ngIf="option.value === 'right'" />
              </kp-menu-item>
            </kp-menu>
          </ng-template>
        </button>
      </sb-wysiwyg-toolbar-group>

      <sb-wysiwyg-colors-selector
        *ngIf="!!colorSet"
        [colorSet]="colorSet"
        [activeColorKey]="format['color']"
        [activeBackgroundKey]="format['background']"
        (onSelectColor)="selectColor($event)"
        (onSelectBackground)="selectBackground($event)"
      />

      <sb-wysiwyg-toolbar-group type="group">
        <button
          sb-wysiwyg-toolbar-item
          *ngFor="let option of styles"
          [active]="
            option.name === 'script'
              ? format[option.name] === option.value
              : !!format[option.name]
          "
          (click.outside-zone)="
            onFormatSelected.emit({
              label: option.label,
              name: option.name,
              value:
                option.name === 'script' ? option.value : !format[option.name],
            })
          "
        >
          <kp-bold-icon *ngIf="option.name === 'bold'" size="small" />
          <kp-italic-icon *ngIf="option.name === 'italic'" size="small" />
          <kp-underline-icon *ngIf="option.name === 'underline'" size="small" />
          <kp-strikethrough-icon
            *ngIf="option.name === 'strike'"
            size="small"
          />
          <kp-subscript-icon
            *ngIf="option.name === 'script' && option.value === 'sub'"
            size="small"
          />
          <kp-superscript-icon
            *ngIf="option.name === 'script' && option.value === 'super'"
            size="small"
          />
        </button>
      </sb-wysiwyg-toolbar-group>

      <!--
        Rendering the emoji picker is a performance bottleneck in places where multiple wysiwy components are rendered.
        Render it only when the toolbar is visible.
      -->
      <sb-wysiwyg-toolbar-group
        *ngIf="show"
        type="single"
        class="sb-wysiwyg-toolbar__emoji"
      >
        <button
          sb-wysiwyg-toolbar-item
          [kp-popover]="emojiContent"
          kp-popover-trigger="click"
          kp-popover-placement="bottom"
        >
          <kp-face-laugh-wink-icon size="small" />

          <ng-template #emojiContent>
            <sb-emoji-picker
              (onEmojiPicked)="onEmojiSelected.emit($event); hidePopovers()"
            />
          </ng-template>
        </button>
      </sb-wysiwyg-toolbar-group>

      <sb-wysiwyg-toolbar-group type="tuple">
        <button
          sb-wysiwyg-toolbar-item
          *ngFor="let option of lists"
          (click.outside-zone)="onFormatSelected.emit(option)"
        >
          <kp-list-ul-icon *ngIf="option.value === 'bullet'" size="small" />
          <kp-list-ol-icon *ngIf="option.value === 'ordered'" size="small" />
        </button>
      </sb-wysiwyg-toolbar-group>

      <sb-wysiwyg-toolbar-group type="tuple">
        <button
          sb-wysiwyg-toolbar-item
          [disabled]="!format['link'] && selection?.text?.length == 0"
          [kp-popover]="linkContent"
          kp-popover-trigger="click"
          kp-popover-placement="bottom"
        >
          <kp-link-icon size="small" />

          <ng-template #linkContent>
            <div class="sb-wysiwyg-toolbar___link-popover">
              <kp-input
                class="sb-wysiwyg-toolbar___link-input"
                type="text"
                size="small"
                placeholder="http://"
                tabindex="-1"
                (changed)="onTypeLink($event)"
              >
                <kp-set kp-input-suffix>
                  <button
                    type="submit"
                    kp-button
                    tone="primary"
                    size="small"
                    (click)="
                      onFormatSelected.emit({
                        label: 'Link',
                        name: 'link',
                        value: linkUrl,
                      });
                      hidePopovers()
                    "
                  >
                    <kp-link-icon />
                  </button>
                </kp-set>
              </kp-input>
            </div>
          </ng-template>
        </button>

        <button
          sb-wysiwyg-toolbar-item
          [disabled]="!format['link']"
          (click.outside-zone)="
            onFormatSelected.emit({
              label: 'Link',
              name: 'link',
              value: false,
            })
          "
        >
          <kp-unlink-icon size="small" />
        </button>
      </sb-wysiwyg-toolbar-group>

      <sb-wysiwyg-toolbar-group type="single">
        <button
          sb-wysiwyg-toolbar-item
          [disabled]="selection?.text?.length == 0"
          (click.outside-zone)="onClearFormats.emit()"
        >
          <kp-text-slash-icon size="small" />
        </button>
      </sb-wysiwyg-toolbar-group>
    </kp-set>
  `,
                    changeDetection: ChangeDetectionStrategy.OnPush,
                }]
        }], ctorParameters: () => [{ type: i0.ElementRef }, { type: i1.TranslateService }], propDecorators: { class: [{
                type: HostBinding,
                args: ['class']
            }], show: [{
                type: Input
            }], textSizes: [{
                type: Input
            }], colorSet: [{
                type: Input
            }], alignments: [{
                type: Input
            }], styles: [{
                type: Input
            }], lists: [{
                type: Input
            }], format: [{
                type: Input
            }], selection: [{
                type: Input
            }], onFormatSelected: [{
                type: Output
            }], onClearFormats: [{
                type: Output
            }], onEmojiSelected: [{
                type: Output
            }], popovers: [{
                type: ViewChildren,
                args: [PopoverBehavior]
            }] } });
const DIRECTIVES$2 = [WysiwygToolbarComponent];
class WysiwygToolbarModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.10", ngImport: i0, type: WysiwygToolbarModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.10", ngImport: i0, type: WysiwygToolbarModule, declarations: [WysiwygToolbarComponent], imports: [CommonModule,
            TranslateModule,
            KappaModule,
            IconsModule,
            OutsideZoneModule,
            WysiwygToolbarItemModule,
            WysiwygColorsSelectorModule,
            EmojiModule], exports: [WysiwygToolbarComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.10", ngImport: i0, type: WysiwygToolbarModule, imports: [CommonModule,
            TranslateModule,
            KappaModule,
            IconsModule,
            OutsideZoneModule,
            WysiwygToolbarItemModule,
            WysiwygColorsSelectorModule,
            EmojiModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.10", ngImport: i0, type: WysiwygToolbarModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        TranslateModule,
                        KappaModule,
                        IconsModule,
                        OutsideZoneModule,
                        WysiwygToolbarItemModule,
                        WysiwygColorsSelectorModule,
                        EmojiModule,
                    ],
                    declarations: DIRECTIVES$2,
                    exports: DIRECTIVES$2,
                }]
        }] });

class LineBreakBlot extends Embed {
    static create() {
        const node = super.create();
        node.innerHTML = '<br>';
        return node;
    }
}
LineBreakBlot.blotName = 'lineBreak';
LineBreakBlot.tagName = 'span';
LineBreakBlot.className = 'line-break';

class BoldBlot extends Inline {
}
BoldBlot.blotName = 'bold';
BoldBlot.tagName = 'b';

class ItalicBlot extends Inline {
}
ItalicBlot.blotName = 'italic';
ItalicBlot.tagName = 'i';

class StrikeBlot extends Inline {
}
StrikeBlot.blotName = 'strike';
StrikeBlot.tagName = 'strike';

AlignStyle.whitelist = ['left', 'center', 'right'];

const DEFAULT_SIZE = 'normal';
const SizeAttributor = new Parchment.ClassAttributor('size', 'textsize', {
    scope: Parchment.Scope.INLINE,
    whitelist: [
        'headline3',
        'headline2',
        'headline',
        'h1',
        'h2',
        'normal0',
        'normal',
        'small',
        'smaller',
        'smaller2',
        'smaller3',
        'smaller4',
    ],
});
const NO_LINE_HEIGHT_CLASS = 'textblock--no-line-height';
/**
 * Applies a line-height of 0 to blocks that contains other text sizes different from the defualt size.
 * This fixes an issue where the line-height of the block gets applied based on the block font size rather than
 * the children font size, resulting on paragraphs with tiny text size but a lot of spacing due to the line-height
 * calculated with the block default font-size.
 * @param block block to apply the line-height fix.
 */
function applyLineHeight(block) {
    const children = block.children;
    if (children.length > 0) {
        const size = children.reduce((current, child) => {
            if (!child || current === DEFAULT_SIZE)
                return DEFAULT_SIZE;
            const sizeAttribute = child.attributes?.attributes?.['size'];
            const value = sizeAttribute
                ? sizeAttribute.value(child.domNode)
                : DEFAULT_SIZE;
            return value;
        }, void 0) ?? DEFAULT_SIZE;
        const node = block.domNode;
        if (size !== DEFAULT_SIZE) {
            if (!node.classList.contains(NO_LINE_HEIGHT_CLASS)) {
                node.classList.add(NO_LINE_HEIGHT_CLASS);
            }
        }
        else {
            if (node.classList.contains(NO_LINE_HEIGHT_CLASS)) {
                node.classList.remove(NO_LINE_HEIGHT_CLASS);
            }
        }
    }
    else {
        const node = block.domNode;
        if (node.classList.contains(NO_LINE_HEIGHT_CLASS)) {
            node.classList.remove(NO_LINE_HEIGHT_CLASS);
        }
    }
}

const COLORS_WHITELIST = [
    'text',
    'back',
    'highlight',
    ...Array.from({ length: 10 }, (_, i) => `chart${i}`),
];
const ColorAttributor = new Parchment.ClassAttributor('color', 'textcolor', {
    scope: Parchment.Scope.INLINE,
    whitelist: COLORS_WHITELIST,
});

const BackgroundAttributor = new Parchment.ClassAttributor('background', 'textbackground', {
    scope: Parchment.Scope.INLINE,
    whitelist: COLORS_WHITELIST,
});

class SmallBlot extends Block {
    static formats() {
        return true;
    }
}
SmallBlot.blotName = 'small';
SmallBlot.tagName = 'p';
SmallBlot.className = 'small';

class EmojiBlot extends Embed {
    static { this.blotName = 'emoji'; }
    static { this.tagName = 'span'; }
    static { this.className = 'ql-emoji'; }
    static create(value) {
        const node = super.create(value);
        node.dataset.emoji = value;
        node.innerHTML = value;
        return node;
    }
    static value(node) {
        return node.dataset.emoji;
    }
}

const ensureProtocol = (url) => !/^https?:\/\//i.test(url) ? `http://${url}` : url;
class LinkBlot extends Link {
    static create(value) {
        const node = super.create(value);
        value = Link.sanitize(value);
        value = ensureProtocol(value);
        node.setAttribute('href', value);
        node.setAttribute('target', '_blank');
        node.setAttribute('rel', 'nofollow ugc noopener noreferrer');
        return node;
    }
    format(name, value) {
        value =
            name === 'link' && typeof value === 'string'
                ? ensureProtocol(value)
                : value;
        super.format(name, value);
    }
}

class ListItem extends ListItemBlot {
    optimize(context) {
        super.optimize(context);
        if (this.children.length >= 1) {
            const child = this.children.head;
            const attributes = child?.attributes?.attributes;
            if (attributes) {
                for (const key in attributes) {
                    const element = attributes[key];
                    const name = element.keyName;
                    const value = element.value(child.domNode);
                    if (name === 'textsize') {
                        const node = this.domNode;
                        const style = `textsize-${value}`;
                        if (!node.classList.contains(style)) {
                            node.setAttribute('class', style);
                        }
                    }
                }
            }
            else {
                const node = this.domNode;
                node.removeAttribute('class');
            }
        }
        applyLineHeight(this);
    }
}

class ScriptBlot extends Inline {
    static create(value) {
        if (value === 'super') {
            return document.createElement('sup');
        }
        else if (value === 'sub') {
            return document.createElement('sub');
        }
        else {
            return super.create(value);
        }
    }
    static formats(domNode) {
        if (domNode.tagName.toLowerCase() === 'sub')
            return 'sub';
        if (domNode.tagName.toLowerCase() === 'sup')
            return 'super';
        return undefined;
    }
}
ScriptBlot.blotName = 'script';
ScriptBlot.tagName = ['sub', 'sup'];

class LineHeightAwareBlock extends Block {
    optimize(context) {
        super.optimize(context);
        applyLineHeight(this);
    }
}

const CUSTOM_BLOTS = [
    LineBreakBlot,
    BoldBlot,
    ItalicBlot,
    StrikeBlot,
    AlignStyle,
    SizeAttributor,
    ColorAttributor,
    BackgroundAttributor,
    SmallBlot,
    EmojiBlot,
    LinkBlot,
    ListItem,
    ScriptBlot,
    LineHeightAwareBlock,
];

var QuillInputComponent_1;
function insertLineBreak(quill, range) {
    if (range.length > 0) {
        quill.deleteText(range.index, range.length, Quill.sources.SILENT);
    }
    quill.insertEmbed(range.index, 'lineBreak', true, Quill.sources.USER);
    quill.setSelection(range.index + 1, Quill.sources.SILENT);
}
const $QuillInput = css({
    display: 'block',
    h: 'auto',
    variants: {
        legacyUI: {
            false: {
                w: '$full',
                py: '$small',
                px: '$medium',
                borderRadius: '$small',
                borderWidth: '$thick',
                borderStyle: 'solid',
                borderColor: '$border',
                '&:hover': {
                    borderColor: '$border-hover',
                },
                '&:focus, &:focus-within': {
                    // override legacy styles in scss file, remove `important` when legacy ui is no longer supported
                    borderColor: '$border-active !important',
                },
                outline: 'none',
                appearance: 'none',
                transitionDuration: '300ms',
                transitionTimingFunction: 'ease',
                transitionProperty: 'border-color, background-color',
            },
        },
    },
});
let QuillInputComponent = class QuillInputComponent {
    static { QuillInputComponent_1 = this; }
    get showLegacyUI() {
        return !!this.legacyUI;
    }
    get isTextSizeBaseH1Enabled() {
        return this.textSizeBase === 'h1';
    }
    get isTextSizeBaseH2Enabled() {
        return this.textSizeBase === 'h2';
    }
    get isTextSizeBaseSmallEnabled() {
        return this.textSizeBase === 'small';
    }
    get value() {
        return this.quill && !this.quill.root.classList.contains('ql-blank')
            ? this.quill.root.innerHTML
            : '';
    }
    set value(text) {
        // If quill is not initialized, it means that this is the first rendering,
        // we capture the input in the initial property to use it and initialize
        // quill text with the initial property value.
        if (!this.quill) {
            this.initial = text;
            return;
        }
        if (this.quill.hasFocus() || text === this.value)
            return;
        this.setText({ text, quill: this.quill, isSilent: true });
    }
    get element() {
        return this.el.nativeElement;
    }
    get css() {
        return propsToCss({
            minHeight: this.minHeight,
        });
    }
    constructor(el, zone) {
        this.el = el;
        this.zone = zone;
        this.history = true;
        this.keyBindings = {};
        this.onChange = new EventEmitter();
        this.onFormatChange = new EventEmitter();
        this.onSelection = new EventEmitter();
        this.onFocus = new EventEmitter();
        this.onBlur = new EventEmitter();
        this.formatChange$ = new Subject();
        this.selectionChange$ = new Subject();
        this.destroy$ = new Subject();
        QuillInputComponent_1.init();
        this.onShiftEnter = this.onShiftEnter.bind(this);
        this.onTextChange = this.onTextChange.bind(this);
        this.onEditorChange = this.onEditorChange.bind(this);
        this.onSelectionChange = this.onSelectionChange.bind(this);
    }
    ngAfterViewInit() {
        this.quill = this.createQuill();
        this.formatChange$
            .pipe(distinctUntilChanged(isEqual), takeUntil(this.destroy$))
            .subscribe(format => this.onFormatChange.emit(format));
        this.selectionChange$
            .pipe(distinctUntilChanged(isEqual), takeUntil(this.destroy$))
            .subscribe(selection => this.onSelection.emit(selection));
    }
    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }
    onShiftEnter(range) {
        insertLineBreak(this.quill, range);
        return false;
    }
    onSelectionChange(range, oldRange) {
        if (range === null) {
            this.onBlur.emit();
        }
        else if (oldRange === null) {
            this.onFocus.emit();
        }
        if (!this.quill || !range)
            return;
        this.formatChange$.next(this.quill.getFormat() || {});
    }
    onTextChange() {
        // This event is triggered when the quill is being created, when the initial value is setup during
        // the after view init lifecycle hook. We should not emit the on change event there, as it would
        // produce ExpressionChangedAfterItHasBeenCheckedError errors during development, becuase this event
        // will change the local value state of the wysiwyg parent component.
        if (!this.quill)
            return;
        this.onChange.emit(this.value);
        this.formatChange$.next(this.quill.getFormat() || {});
    }
    onEditorChange(event, range) {
        if (event !== 'selection-change' || !this.quill || !range)
            return;
        // We listen to selection change here instead of OnSelectionChange event handler
        // because we would get silent events here.
        const text = this.quill.getText(range.index, range.length) || '';
        this.selectionChange$.next({ range, text: text === '\n' ? '' : text });
    }
    addContainer(el) {
        if (!this.quill)
            return;
        this.quill.addContainer(el);
    }
    focus() {
        if (!this.quill)
            return;
        this.quill.setSelection(this.quill.getLength(), 0);
    }
    blur() {
        if (!this.quill)
            return;
        this.quill.setSelection(null, 0);
    }
    format(name, value) {
        if (name === 'link')
            this.formatLink(value);
        else
            this.quill.format(name, value, Quill.sources.USER);
        this.formatChange$.next(this.quill.getFormat() || {});
    }
    insertText(text) {
        const range = this.quill.getSelection() || EMPTY_RANGE;
        if (range.length > 0) {
            this.quill.deleteText(range.index, range.length, Quill.sources.SILENT);
        }
        this.quill.insertText(range.index, text, Quill.sources.USER);
        this.quill.setSelection(range.index + 1, Quill.sources.SILENT);
    }
    insertEmoji(value, point) {
        const range = point || this.quill.getSelection() || EMPTY_RANGE;
        if (range.length > 0) {
            this.quill.deleteText(range.index, range.length, Quill.sources.SILENT);
        }
        this.insertEmbed('emoji', value, range);
        this.quill.setSelection(range.index + 1, Quill.sources.SILENT);
    }
    insertEmbed(name, value, point) {
        const range = point || this.quill.getSelection() || EMPTY_RANGE;
        this.quill.insertEmbed(range.index, name, value, Quill.sources.USER);
    }
    clearFormat() {
        const range = this.quill.getSelection() || EMPTY_RANGE;
        if (range.length > 0) {
            this.quill.removeFormat(range.index, range.length, Quill.sources.USER);
            this.formatChange$.next(this.quill.getFormat() || {});
        }
    }
    formatLink(value) {
        const selection = this.quill.getSelection() || EMPTY_RANGE;
        if (value === false && selection.length === 0) {
            const selectionIndex = Math.max(0, selection.index - 1);
            // When value is false (unlink) and there is no selection,
            // look for a nearby link to remove it
            const [link, offset] = this.quill.scroll.descendant(LinkBlot, selectionIndex);
            if (link) {
                this.quill.formatText(selectionIndex - offset, link.length(), 'link', value, Quill.sources.USER);
                return;
            }
        }
        this.quill.format('link', value, Quill.sources.USER);
    }
    createQuill() {
        return this.zone.runOutsideAngular(() => {
            const keyBindings = Object.assign(this.keyBindings || {}, {
                shiftEnter: {
                    key: 'Enter',
                    shiftKey: true,
                    handler: this.onShiftEnter,
                },
            });
            const quill = new Quill(this.el.nativeElement, {
                debug: 'error',
                placeholder: this.placeholder,
                modules: {
                    history: { maxStack: this.history ? 100 : 0 },
                    keyboard: {
                        bindings: keyBindings,
                    },
                },
                formats: [
                    'size',
                    'color',
                    'background',
                    'bold',
                    'italic',
                    'underline',
                    'strike',
                    'script',
                    'link',
                    'list',
                    'indent',
                    'align',
                    'lineBreak',
                    'emoji',
                    // Deprecated:
                    'header',
                    'small',
                ],
            });
            quill.on(Quill.events.TEXT_CHANGE, this.onTextChange);
            quill.on(Quill.events.EDITOR_CHANGE, this.onEditorChange);
            quill.on(Quill.events.SELECTION_CHANGE, this.onSelectionChange);
            if (this.initial !== undefined && this.initial !== null) {
                this.setText({ text: this.initial, quill, isSilent: true });
                this.initial = void 0;
            }
            return quill;
        });
    }
    setText({ text, quill, isSilent = false, }) {
        if (!quill)
            return;
        const pasteAndBlur = () => {
            const hasFocus = quill.hasFocus();
            quill.clipboard.dangerouslyPasteHTML(text || '', isSilent ? Quill.sources.SILENT : undefined);
            if (!hasFocus)
                quill.blur();
        };
        if (this.el && this.el.nativeElement) {
            const scrollContainer = findScrollContainer(this.el.nativeElement);
            if (scrollContainer) {
                const { scrollLeft, scrollTop } = scrollContainer;
                pasteAndBlur();
                scrollContainer.scrollLeft = scrollLeft;
                scrollContainer.scrollTop = scrollTop;
                return;
            }
        }
        pasteAndBlur();
    }
    static { this.isInit = false; }
    static init() {
        if (QuillInputComponent_1.isInit)
            return;
        // Register custom blots
        CUSTOM_BLOTS.forEach(b => Quill.register(b, true));
        QuillInputComponent_1.isInit = true;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.10", ngImport: i0, type: QuillInputComponent, deps: [{ token: i0.ElementRef }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.10", type: QuillInputComponent, selector: "sb-quill-input", inputs: { textSizeBase: "textSizeBase", placeholder: "placeholder", history: "history", legacyUI: "legacyUI", minHeight: "minHeight", keyBindings: "keyBindings", value: "value" }, outputs: { onChange: "onChange", onFormatChange: "onFormatChange", onSelection: "onSelection", onFocus: "onFocus", onBlur: "onBlur" }, host: { properties: { "class": "this.class", "class.form-control": "this.showLegacyUI", "class.textsize-base--h1": "this.isTextSizeBaseH1Enabled", "class.textsize-base--h2": "this.isTextSizeBaseH2Enabled", "class.textsize-base--small": "this.isTextSizeBaseSmallEnabled" } }, ngImport: i0, template: '', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
};
__decorate([
    CoerceBoolean()
], QuillInputComponent.prototype, "history", void 0);
__decorate([
    CoerceBoolean()
], QuillInputComponent.prototype, "legacyUI", void 0);
QuillInputComponent = QuillInputComponent_1 = __decorate([
    Styled($QuillInput)
], QuillInputComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.10", ngImport: i0, type: QuillInputComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'sb-quill-input',
                    template: '',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                }]
        }], ctorParameters: () => [{ type: i0.ElementRef }, { type: i0.NgZone }], propDecorators: { class: [{
                type: HostBinding,
                args: ['class']
            }], showLegacyUI: [{
                type: HostBinding,
                args: ['class.form-control']
            }], isTextSizeBaseH1Enabled: [{
                type: HostBinding,
                args: ['class.textsize-base--h1']
            }], isTextSizeBaseH2Enabled: [{
                type: HostBinding,
                args: ['class.textsize-base--h2']
            }], isTextSizeBaseSmallEnabled: [{
                type: HostBinding,
                args: ['class.textsize-base--small']
            }], textSizeBase: [{
                type: Input
            }], placeholder: [{
                type: Input
            }], history: [{
                type: Input
            }], legacyUI: [{
                type: Input
            }], minHeight: [{
                type: Input
            }], keyBindings: [{
                type: Input
            }], value: [{
                type: Input
            }], onChange: [{
                type: Output
            }], onFormatChange: [{
                type: Output
            }], onSelection: [{
                type: Output
            }], onFocus: [{
                type: Output
            }], onBlur: [{
                type: Output
            }] } });
const DIRECTIVES$1 = [QuillInputComponent];
class QuillInputModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.10", ngImport: i0, type: QuillInputModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.10", ngImport: i0, type: QuillInputModule, declarations: [QuillInputComponent], imports: [CommonModule, KappaModule], exports: [QuillInputComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.10", ngImport: i0, type: QuillInputModule, imports: [CommonModule, KappaModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.10", ngImport: i0, type: QuillInputModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule, KappaModule],
                    declarations: DIRECTIVES$1,
                    exports: DIRECTIVES$1,
                }]
        }] });

class WysiwygService {
    constructor() {
        this.onWysiwygShow$ = new Subject();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.10", ngImport: i0, type: WysiwygService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.10", ngImport: i0, type: WysiwygService }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.10", ngImport: i0, type: WysiwygService, decorators: [{
            type: Injectable
        }] });

const HEADING_1_TEXT_SIZE = {
    label: 'Heading 1',
    name: 'size',
    value: 'headline3',
    unit: '80pt',
};
const HEADING_2_TEXT_SIZE = {
    label: 'Heading 2',
    name: 'size',
    value: 'headline2',
    unit: '67pt',
};
const HEADING_3_TEXT_SIZE = {
    label: 'Heading 3',
    name: 'size',
    value: 'headline',
    unit: '54pt',
};
const HEADING_4_TEXT_SIZE = {
    label: 'Heading 4',
    name: 'size',
    value: 'h1',
    unit: '42pt',
};
const HEADING_5_TEXT_SIZE = {
    label: 'Heading 5',
    name: 'size',
    value: 'h2',
    unit: '32pt',
};
const BODY_1_TEXT_SIZE = {
    label: 'Body 1',
    name: 'size',
    value: 'normal0',
    unit: '31pt',
};
const BODY_2_TEXT_SIZE = {
    label: 'Body 2',
    name: 'size',
    value: false,
    unit: '27pt',
};
const BODY_3_TEXT_SIZE = {
    label: 'Body 3',
    name: 'size',
    value: 'small',
    unit: '20pt',
};
const BODY_4_TEXT_SIZE = {
    label: 'Body 4',
    name: 'size',
    value: 'smaller',
    unit: '16pt',
};
const BODY_5_TEXT_SIZE = {
    label: 'Body 5',
    name: 'size',
    value: 'smaller2',
    unit: '14pt',
};
const BODY_6_TEXT_SIZE = {
    label: 'Body 6',
    name: 'size',
    value: 'smaller3',
    unit: '10pt',
};
const TEXT_SIZES = [
    HEADING_1_TEXT_SIZE,
    HEADING_2_TEXT_SIZE,
    HEADING_3_TEXT_SIZE,
    HEADING_4_TEXT_SIZE,
    HEADING_5_TEXT_SIZE,
    BODY_1_TEXT_SIZE,
    BODY_2_TEXT_SIZE,
    BODY_3_TEXT_SIZE,
    BODY_4_TEXT_SIZE,
    BODY_5_TEXT_SIZE,
    BODY_6_TEXT_SIZE,
];
const LEFT_ALIGNMENT = { label: 'Left', name: 'align', value: 'left' };
const CENTER_ALIGNMENT = {
    label: 'Center',
    name: 'align',
    value: 'center',
};
const RIGHT_ALIGNMENT = {
    label: 'Right',
    name: 'align',
    value: 'right',
};
const ALIGNMENTS = [LEFT_ALIGNMENT, CENTER_ALIGNMENT, RIGHT_ALIGNMENT];
const BOLD_TEXT_STYLE = { label: 'Bold', name: 'bold', value: false };
const ITALIC_TEXT_STYLE = {
    label: 'Italic',
    name: 'italic',
    value: false,
};
const UNDERLINE_TEXT_STYLE = {
    label: 'Underline',
    name: 'underline',
    value: false,
};
const STRIKE_TEXT_STYLE = {
    label: 'Strike',
    name: 'strike',
    value: false,
};
const SUBSCRIPT_TEXT_STYLE = {
    label: 'Subscript',
    name: 'script',
    value: 'sub',
};
const SUPERSCRIPT_TEXT_STYLE = {
    label: 'Superscript',
    name: 'script',
    value: 'super',
};
const STYLES = [
    BOLD_TEXT_STYLE,
    ITALIC_TEXT_STYLE,
    UNDERLINE_TEXT_STYLE,
    STRIKE_TEXT_STYLE,
    SUBSCRIPT_TEXT_STYLE,
    SUPERSCRIPT_TEXT_STYLE,
];
const BULLET_LIST = {
    label: 'Bullet List',
    name: 'list',
    value: 'bullet',
};
const ORDERED_LIST = {
    label: 'Ordered List',
    name: 'list',
    value: 'ordered',
};
const LISTS = [BULLET_LIST, ORDERED_LIST];

const TOGGLES = ['bold', 'italic', 'underline', 'strike', 'list', 'script'];
const $Wysiwyg = css({
    display: 'block',
    'hy-popper-host': {
        'kp-popover': {
            p: '$xsmall',
        },
    },
});
let WysiwygComponent = class WysiwygComponent {
    // TODO: This z-index value is to avoid other componets going over the popovers.
    // Before it had a value of 9999, but that was way to high, causing problems with other
    // components like modals. I settled with 105 because is the smallest value that would
    // work alongside the hot spreadsheet component.
    get zIndex() {
        return this.isToolbarOpen ? 105 : 0;
    }
    get isToolbarOpen() {
        return this.isToolbarOpen$.getValue();
    }
    set isToolbarOpen(value) {
        if (value === this.isToolbarOpen)
            return;
        this.isToolbarOpen$.next(value);
    }
    get value() {
        return this.value$.getValue();
    }
    set value(raw) {
        const value = raw || '';
        if (value === this.value)
            return;
        this.value$.next(value);
    }
    get textSizeEnabled() {
        return this.textSizes.length > 0;
    }
    set textSizeEnabled(value) {
        if (value === this.textSizeEnabled)
            return;
        this.textSizes = value ? TEXT_SIZES : [];
    }
    get alignmentEnabled() {
        return this.alignments.length > 0;
    }
    set alignmentEnabled(value) {
        if (value === this.alignmentEnabled)
            return;
        this.alignments = value ? ALIGNMENTS : [];
    }
    get element() {
        return this.el.nativeElement;
    }
    constructor(el, zone, detector, wysiwygService) {
        this.el = el;
        this.zone = zone;
        this.detector = detector;
        this.wysiwygService = wysiwygService;
        this.toolbarButtonsWrap = true;
        this.toolbarAlignment = 'left';
        this.defaultAlignment = 'left';
        this.textSizes = [];
        this.alignments = [];
        this.styles = STYLES;
        this.lists = LISTS;
        this.keyBindings = {};
        this.history = true;
        this.onChange = new EventEmitter();
        this.onFocus = new EventEmitter();
        this.onBlur = new EventEmitter();
        this.value$ = new BehaviorSubject('');
        this.isToolbarOpen$ = new BehaviorSubject(false);
        this.destroy$ = new Subject();
        this.format$ = new BehaviorSubject({
            align: this.defaultAlignment,
            link: '',
        });
        this.selection$ = new BehaviorSubject(EMPTY_SELECTION);
    }
    ngOnInit() {
        const format = this.format$.getValue();
        if (format.align !== this.defaultAlignment) {
            this.format$.next({ ...format, align: this.defaultAlignment });
        }
    }
    ngAfterViewInit() {
        this.editor.addContainer(this.toolbar.element.nativeElement);
        fromEvent(document, 'click')
            .pipe(withLatestFrom(this.isToolbarOpen$.pipe(debounceTime(10))), 
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        filter(([_, isToolbarOpen]) => isToolbarOpen), takeUntil(this.destroy$))
            .subscribe(([e]) => this.onClickOutside(e));
        this.isToolbarOpen$
            .pipe(distinctUntilChanged(), takeUntil(this.destroy$))
            .subscribe(isToolbarOpen => {
            if (this.toolbar)
                this.toolbar.hidePopovers();
            if (isToolbarOpen)
                this.wysiwygService.onWysiwygShow$.next(this);
            this.detector.detectChanges();
        });
        // Hide toolbar when another component toolbar shows
        this.wysiwygService.onWysiwygShow$
            .pipe(takeUntil(this.destroy$))
            .subscribe(component => {
            if (this === component)
                return;
            this.isToolbarOpen$.next(false);
        });
        merge(this.format$.pipe(distinctUntilChanged(isEqual)), this.selection$.pipe(distinctUntilChanged(isEqual)), this.value$.pipe(distinctUntilChanged()))
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => this.detector.detectChanges());
    }
    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }
    onFormatChange(format) {
        this.format$.next({ align: this.defaultAlignment, link: '', ...format });
    }
    onSelectionChange(selection) {
        this.selection$.next(selection);
        this.detector.detectChanges();
    }
    onFormatSelected(option) {
        if (!this.editor)
            return;
        if (!TOGGLES.includes(option.name)) {
            this.editor.format(option.name, option.value);
            return;
        }
        const format = this.format$.getValue();
        this.editor.format(option.name, format[option.name] !== option.value ? option.value : false);
    }
    onEmojiSelected(emoji) {
        if (!this.editor)
            return;
        const selection = this.selection$.getValue();
        this.editor.insertEmoji(emoji, selection && selection.range);
    }
    onClearFormats() {
        if (!this.editor)
            return;
        this.editor.clearFormat();
    }
    onValueChange(value) {
        this.value = value;
        this.zone.run(() => this.onChange.emit(value));
    }
    onEditorFocus() {
        this.onFocus.emit();
        this.isToolbarOpen$.next(true);
    }
    setValue(value) {
        this.value = value;
    }
    getValue() {
        return this.value;
    }
    focus() {
        if (!this.editor)
            return;
        this.editor.focus();
    }
    blur() {
        if (!this.editor)
            return;
        this.editor.blur();
    }
    onClickOutside(event) {
        const target = /touch/.test(event.type)
            ? event.targetTouches[0]
            : event.target;
        if (!this.element.contains(document.activeElement) &&
            !this.element.contains(target)) {
            this.isToolbarOpen$.next(false);
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.10", ngImport: i0, type: WysiwygComponent, deps: [{ token: i0.ElementRef }, { token: i0.NgZone }, { token: i0.ChangeDetectorRef }, { token: WysiwygService }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.10", type: WysiwygComponent, selector: "sb-wysiwyg", inputs: { placeholder: "placeholder", minHeight: "minHeight", textSizeBase: "textSizeBase", toolbarButtonsWrap: "toolbarButtonsWrap", toolbarAlignment: "toolbarAlignment", defaultAlignment: "defaultAlignment", textSizes: "textSizes", colorSet: "colorSet", alignments: "alignments", styles: "styles", lists: "lists", keyBindings: "keyBindings", history: "history", legacyInputUI: "legacyInputUI", isToolbarOpen: "isToolbarOpen", value: "value", textSizeEnabled: "textSizeEnabled", alignmentEnabled: "alignmentEnabled" }, outputs: { onChange: "onChange", onFocus: "onFocus", onBlur: "onBlur" }, host: { properties: { "class": "this.class", "style.z-index": "this.zIndex" } }, viewQueries: [{ propertyName: "editor", first: true, predicate: QuillInputComponent, descendants: true, static: true }, { propertyName: "toolbar", first: true, predicate: WysiwygToolbarComponent, descendants: true, static: true }], ngImport: i0, template: `
    <sb-wysiwyg-toolbar
      [show]="isToolbarOpen"
      [format]="format$ | async"
      [selection]="selection$ | async"
      [textSizes]="textSizes"
      [colorSet]="colorSet"
      [alignments]="alignments"
      [styles]="styles"
      [lists]="lists"
      (onFormatSelected)="onFormatSelected($event)"
      (onEmojiSelected)="onEmojiSelected($event)"
      (onClearFormats)="onClearFormats()"
    />

    <sb-quill-input
      [minHeight]="minHeight"
      [legacyUI]="legacyInputUI"
      [value]="value"
      [placeholder]="placeholder"
      [keyBindings]="keyBindings"
      [textSizeBase]="textSizeBase"
      [history]="history"
      (onChange)="onValueChange($event)"
      (onFocus)="onEditorFocus()"
      (onBlur)="onBlur.emit($event)"
      (onFormatChange)="onFormatChange($event)"
      (onSelection)="onSelectionChange($event)"
    />
  `, isInline: true, styles: [":host{display:block;position:relative}:host ::ng-deep .ql-container{font-family:Nunito Sans,Helvetica Neue,Arial,sans-serif;display:flex;flex-flow:column nowrap}:host ::ng-deep .ql-container:focus-within{background-color:#fff;border-color:#52bef2}:host ::ng-deep .ql-container .ql-editor{flex:1 1 auto;padding:0;margin:0}:host ::ng-deep .ql-container .ql-editor.ql-blank:before{color:#868e96;font-style:normal}:host ::ng-deep .ql-container .ql-editor>*{line-height:1.2;max-width:100%;margin:0 0 .75em}:host ::ng-deep .ql-container .ql-editor>*:last-child{margin-bottom:0}:host ::ng-deep .ql-container .ql-editor h1:not(:first-child),:host ::ng-deep .ql-container .ql-editor h2:not(:first-child),:host ::ng-deep .ql-container .ql-editor h3:not(:first-child),:host ::ng-deep .ql-container .ql-editor h4:not(:first-child),:host ::ng-deep .ql-container .ql-editor h5:not(:first-child),:host ::ng-deep .ql-container .ql-editor h6:not(:first-child){margin-top:.75em}:host ::ng-deep .ql-container .ql-editor h1+h2{margin-top:-.5em}:host ::ng-deep .ql-container .ql-editor p.small:not(:last-child){margin-bottom:1em}:host ::ng-deep .ql-container .ql-editor sub,:host ::ng-deep .ql-container .ql-editor sup{position:relative;font-size:70%;line-height:0;vertical-align:baseline}:host ::ng-deep .ql-container .ql-editor sub{bottom:-.2em}:host ::ng-deep .ql-container .ql-editor sup{top:-.4em}:host ::ng-deep .ql-container .ql-editor h1{font-size:1.6em}:host ::ng-deep .ql-container .ql-editor h2{font-size:1.2em}:host ::ng-deep .ql-container .ql-editor p.small{font-size:.75em}:host ::ng-deep .ql-container .ql-editor .textblock--no-line-height{line-height:0}:host ::ng-deep .ql-container .ql-editor .textsize-headline3:not(li){font-size:2.986em;line-height:1.2}:host ::ng-deep .ql-container .ql-editor li.textsize-headline3:before,:host ::ng-deep .ql-container .ql-editor li.textsize-headline3>.ql-ui:first-child:before{font-size:2.986em;line-height:1.2}:host ::ng-deep .ql-container .ql-editor sub.textsize-headline3,:host ::ng-deep .ql-container .ql-editor sup.textsize-headline3{font-size:2.0902em}:host ::ng-deep .ql-container .ql-editor .textsize-headline2:not(li){font-size:2.488em;line-height:1.2}:host ::ng-deep .ql-container .ql-editor li.textsize-headline2:before,:host ::ng-deep .ql-container .ql-editor li.textsize-headline2>.ql-ui:first-child:before{font-size:2.488em;line-height:1.2}:host ::ng-deep .ql-container .ql-editor sub.textsize-headline2,:host ::ng-deep .ql-container .ql-editor sup.textsize-headline2{font-size:1.7416em}:host ::ng-deep .ql-container .ql-editor .textsize-headline:not(li){font-size:2em;line-height:1.2}:host ::ng-deep .ql-container .ql-editor li.textsize-headline:before,:host ::ng-deep .ql-container .ql-editor li.textsize-headline>.ql-ui:first-child:before{font-size:2em;line-height:1.2}:host ::ng-deep .ql-container .ql-editor sub.textsize-headline,:host ::ng-deep .ql-container .ql-editor sup.textsize-headline{font-size:1.4em}:host ::ng-deep .ql-container .ql-editor .textsize-h1:not(li){font-size:1.6em;line-height:1.2}:host ::ng-deep .ql-container .ql-editor li.textsize-h1:before,:host ::ng-deep .ql-container .ql-editor li.textsize-h1>.ql-ui:first-child:before{font-size:1.6em;line-height:1.2}:host ::ng-deep .ql-container .ql-editor sub.textsize-h1,:host ::ng-deep .ql-container .ql-editor sup.textsize-h1{font-size:1.12em}:host ::ng-deep .ql-container .ql-editor .textsize-h2:not(li){font-size:1.2em;line-height:1.2}:host ::ng-deep .ql-container .ql-editor li.textsize-h2:before,:host ::ng-deep .ql-container .ql-editor li.textsize-h2>.ql-ui:first-child:before{font-size:1.2em;line-height:1.2}:host ::ng-deep .ql-container .ql-editor sub.textsize-h2,:host ::ng-deep .ql-container .ql-editor sup.textsize-h2{font-size:.84em}:host ::ng-deep .ql-container .ql-editor .textsize-normal0:not(li){font-size:1.15em;line-height:1.2}:host ::ng-deep .ql-container .ql-editor li.textsize-normal0:before,:host ::ng-deep .ql-container .ql-editor li.textsize-normal0>.ql-ui:first-child:before{font-size:1.15em;line-height:1.2}:host ::ng-deep .ql-container .ql-editor sub.textsize-normal0,:host ::ng-deep .ql-container .ql-editor sup.textsize-normal0{font-size:.805em}:host ::ng-deep .ql-container .ql-editor .textsize-normal:not(li){font-size:1em;line-height:1.2}:host ::ng-deep .ql-container .ql-editor li.textsize-normal:before,:host ::ng-deep .ql-container .ql-editor li.textsize-normal>.ql-ui:first-child:before{font-size:1em;line-height:1.2}:host ::ng-deep .ql-container .ql-editor sub.textsize-normal,:host ::ng-deep .ql-container .ql-editor sup.textsize-normal{font-size:.7em}:host ::ng-deep .ql-container .ql-editor .textsize-small:not(li){font-size:.75em;line-height:1.2}:host ::ng-deep .ql-container .ql-editor li.textsize-small:before,:host ::ng-deep .ql-container .ql-editor li.textsize-small>.ql-ui:first-child:before{font-size:.75em;line-height:1.2}:host ::ng-deep .ql-container .ql-editor sub.textsize-small,:host ::ng-deep .ql-container .ql-editor sup.textsize-small{font-size:.525em}:host ::ng-deep .ql-container .ql-editor .textsize-smaller:not(li){font-size:.6em;line-height:1.2}:host ::ng-deep .ql-container .ql-editor li.textsize-smaller:before,:host ::ng-deep .ql-container .ql-editor li.textsize-smaller>.ql-ui:first-child:before{font-size:.6em;line-height:1.2}:host ::ng-deep .ql-container .ql-editor sub.textsize-smaller,:host ::ng-deep .ql-container .ql-editor sup.textsize-smaller{font-size:.42em}:host ::ng-deep .ql-container .ql-editor .textsize-smaller2:not(li){font-size:.512em;line-height:1.2}:host ::ng-deep .ql-container .ql-editor li.textsize-smaller2:before,:host ::ng-deep .ql-container .ql-editor li.textsize-smaller2>.ql-ui:first-child:before{font-size:.512em;line-height:1.2}:host ::ng-deep .ql-container .ql-editor sub.textsize-smaller2,:host ::ng-deep .ql-container .ql-editor sup.textsize-smaller2{font-size:.3584em}:host ::ng-deep .ql-container .ql-editor .textsize-smaller3:not(li){font-size:.402em;line-height:1.2}:host ::ng-deep .ql-container .ql-editor li.textsize-smaller3:before,:host ::ng-deep .ql-container .ql-editor li.textsize-smaller3>.ql-ui:first-child:before{font-size:.402em;line-height:1.2}:host ::ng-deep .ql-container .ql-editor sub.textsize-smaller3,:host ::ng-deep .ql-container .ql-editor sup.textsize-smaller3{font-size:.2814em}:host ::ng-deep .ql-container .ql-editor ul,:host ::ng-deep .ql-container .ql-editor ol{list-style-position:inside;padding-left:.75em}:host ::ng-deep .ql-container .ql-editor ul li,:host ::ng-deep .ql-container .ql-editor ol li{position:static;margin-bottom:.25em;padding-left:1.5em}:host ::ng-deep .ql-container .ql-editor ul li[data-list]>.ql-ui:first-child,:host ::ng-deep .ql-container .ql-editor ol li[data-list]>.ql-ui:first-child{position:static}:host ::ng-deep .ql-container .ql-editor ul li:last-child,:host ::ng-deep .ql-container .ql-editor ol li:last-child{margin-bottom:0}:host ::ng-deep .ql-container .ql-editor ul li.ql-indent-1,:host ::ng-deep .ql-container .ql-editor ol li.ql-indent-1{padding-left:3em}:host ::ng-deep .ql-container .ql-editor ul li.ql-indent-2,:host ::ng-deep .ql-container .ql-editor ol li.ql-indent-2{padding-left:4.5em}:host ::ng-deep .ql-container .ql-editor ul li.ql-indent-3,:host ::ng-deep .ql-container .ql-editor ol li.ql-indent-3{padding-left:6em}:host ::ng-deep .ql-container .ql-editor ul li.ql-indent-4,:host ::ng-deep .ql-container .ql-editor ol li.ql-indent-4{padding-left:7.5em}:host ::ng-deep .ql-container .ql-editor ul li.ql-indent-5,:host ::ng-deep .ql-container .ql-editor ol li.ql-indent-5{padding-left:9em}:host ::ng-deep .ql-container .ql-editor ul li.ql-indent-6,:host ::ng-deep .ql-container .ql-editor ol li.ql-indent-6{padding-left:10.5em}:host ::ng-deep .ql-container .ql-editor ul li.ql-indent-7,:host ::ng-deep .ql-container .ql-editor ol li.ql-indent-7{padding-left:12em}:host ::ng-deep .ql-container .ql-editor ul li.ql-indent-8,:host ::ng-deep .ql-container .ql-editor ol li.ql-indent-8{padding-left:13.5em}:host ::ng-deep .ql-container .ql-editor [class^=textcolor-],:host ::ng-deep .ql-container .ql-editor [class*=\" textcolor-\"]{color:#868e96}:host ::ng-deep .ql-container .ql-editor [class^=textbackground-],:host ::ng-deep .ql-container .ql-editor [class*=\" textbackground-\"]{background-color:#e9ecef}:host ::ng-deep .ql-container.textsize-base--headline3 .ql-editor{font-size:2.986em}:host ::ng-deep .ql-container.textsize-base--headline2 .ql-editor{font-size:2.488em}:host ::ng-deep .ql-container.textsize-base--headline .ql-editor{font-size:2em}:host ::ng-deep .ql-container.textsize-base--h1 .ql-editor{font-size:1.6em}:host ::ng-deep .ql-container.textsize-base--h2 .ql-editor{font-size:1.2em}:host ::ng-deep .ql-container.textsize-base--small .ql-editor{font-size:.75em}:host ::ng-deep .ql-container.textsize-base--small .ql-editor .textsize-headline3{font-size:3.9813333333em}:host ::ng-deep .ql-container.textsize-base--small .ql-editor .textsize-headline2{font-size:3.3173333333em}:host ::ng-deep .ql-container.textsize-base--small .ql-editor .textsize-headline{font-size:2.6666666667em}:host ::ng-deep .ql-container.textsize-base--small .ql-editor .textsize-h1{font-size:2.1333333333em}:host ::ng-deep .ql-container.textsize-base--small .ql-editor .textsize-h2{font-size:1.6em}:host ::ng-deep .ql-container.textsize-base--small .ql-editor .textsize-normal0{font-size:1.5333333333em}:host ::ng-deep .ql-container.textsize-base--small .ql-editor .textsize-normal{font-size:1.3333333333em}:host ::ng-deep .ql-container.textsize-base--small .ql-editor .textsize-small{font-size:1em}:host ::ng-deep .ql-container.textsize-base--small .ql-editor .textsize-smaller{font-size:.8em}:host ::ng-deep .ql-container.textsize-base--small .ql-editor .textsize-smaller2{font-size:.6826666667em}:host ::ng-deep .ql-container.textsize-base--small .ql-editor .textsize-smaller3{font-size:.536em}\n"], dependencies: [{ kind: "component", type: WysiwygToolbarComponent, selector: "sb-wysiwyg-toolbar", inputs: ["show", "textSizes", "colorSet", "alignments", "styles", "lists", "format", "selection"], outputs: ["onFormatSelected", "onClearFormats", "onEmojiSelected"] }, { kind: "component", type: QuillInputComponent, selector: "sb-quill-input", inputs: ["textSizeBase", "placeholder", "history", "legacyUI", "minHeight", "keyBindings", "value"], outputs: ["onChange", "onFormatChange", "onSelection", "onFocus", "onBlur"] }, { kind: "pipe", type: i2.AsyncPipe, name: "async" }], changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
};
__decorate([
    CoerceBoolean()
], WysiwygComponent.prototype, "toolbarButtonsWrap", void 0);
__decorate([
    CoerceBoolean()
], WysiwygComponent.prototype, "history", void 0);
__decorate([
    CoerceBoolean()
], WysiwygComponent.prototype, "legacyInputUI", void 0);
WysiwygComponent = __decorate([
    Styled($Wysiwyg)
], WysiwygComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.10", ngImport: i0, type: WysiwygComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sb-wysiwyg', template: `
    <sb-wysiwyg-toolbar
      [show]="isToolbarOpen"
      [format]="format$ | async"
      [selection]="selection$ | async"
      [textSizes]="textSizes"
      [colorSet]="colorSet"
      [alignments]="alignments"
      [styles]="styles"
      [lists]="lists"
      (onFormatSelected)="onFormatSelected($event)"
      (onEmojiSelected)="onEmojiSelected($event)"
      (onClearFormats)="onClearFormats()"
    />

    <sb-quill-input
      [minHeight]="minHeight"
      [legacyUI]="legacyInputUI"
      [value]="value"
      [placeholder]="placeholder"
      [keyBindings]="keyBindings"
      [textSizeBase]="textSizeBase"
      [history]="history"
      (onChange)="onValueChange($event)"
      (onFocus)="onEditorFocus()"
      (onBlur)="onBlur.emit($event)"
      (onFormatChange)="onFormatChange($event)"
      (onSelection)="onSelectionChange($event)"
    />
  `, changeDetection: ChangeDetectionStrategy.OnPush, styles: [":host{display:block;position:relative}:host ::ng-deep .ql-container{font-family:Nunito Sans,Helvetica Neue,Arial,sans-serif;display:flex;flex-flow:column nowrap}:host ::ng-deep .ql-container:focus-within{background-color:#fff;border-color:#52bef2}:host ::ng-deep .ql-container .ql-editor{flex:1 1 auto;padding:0;margin:0}:host ::ng-deep .ql-container .ql-editor.ql-blank:before{color:#868e96;font-style:normal}:host ::ng-deep .ql-container .ql-editor>*{line-height:1.2;max-width:100%;margin:0 0 .75em}:host ::ng-deep .ql-container .ql-editor>*:last-child{margin-bottom:0}:host ::ng-deep .ql-container .ql-editor h1:not(:first-child),:host ::ng-deep .ql-container .ql-editor h2:not(:first-child),:host ::ng-deep .ql-container .ql-editor h3:not(:first-child),:host ::ng-deep .ql-container .ql-editor h4:not(:first-child),:host ::ng-deep .ql-container .ql-editor h5:not(:first-child),:host ::ng-deep .ql-container .ql-editor h6:not(:first-child){margin-top:.75em}:host ::ng-deep .ql-container .ql-editor h1+h2{margin-top:-.5em}:host ::ng-deep .ql-container .ql-editor p.small:not(:last-child){margin-bottom:1em}:host ::ng-deep .ql-container .ql-editor sub,:host ::ng-deep .ql-container .ql-editor sup{position:relative;font-size:70%;line-height:0;vertical-align:baseline}:host ::ng-deep .ql-container .ql-editor sub{bottom:-.2em}:host ::ng-deep .ql-container .ql-editor sup{top:-.4em}:host ::ng-deep .ql-container .ql-editor h1{font-size:1.6em}:host ::ng-deep .ql-container .ql-editor h2{font-size:1.2em}:host ::ng-deep .ql-container .ql-editor p.small{font-size:.75em}:host ::ng-deep .ql-container .ql-editor .textblock--no-line-height{line-height:0}:host ::ng-deep .ql-container .ql-editor .textsize-headline3:not(li){font-size:2.986em;line-height:1.2}:host ::ng-deep .ql-container .ql-editor li.textsize-headline3:before,:host ::ng-deep .ql-container .ql-editor li.textsize-headline3>.ql-ui:first-child:before{font-size:2.986em;line-height:1.2}:host ::ng-deep .ql-container .ql-editor sub.textsize-headline3,:host ::ng-deep .ql-container .ql-editor sup.textsize-headline3{font-size:2.0902em}:host ::ng-deep .ql-container .ql-editor .textsize-headline2:not(li){font-size:2.488em;line-height:1.2}:host ::ng-deep .ql-container .ql-editor li.textsize-headline2:before,:host ::ng-deep .ql-container .ql-editor li.textsize-headline2>.ql-ui:first-child:before{font-size:2.488em;line-height:1.2}:host ::ng-deep .ql-container .ql-editor sub.textsize-headline2,:host ::ng-deep .ql-container .ql-editor sup.textsize-headline2{font-size:1.7416em}:host ::ng-deep .ql-container .ql-editor .textsize-headline:not(li){font-size:2em;line-height:1.2}:host ::ng-deep .ql-container .ql-editor li.textsize-headline:before,:host ::ng-deep .ql-container .ql-editor li.textsize-headline>.ql-ui:first-child:before{font-size:2em;line-height:1.2}:host ::ng-deep .ql-container .ql-editor sub.textsize-headline,:host ::ng-deep .ql-container .ql-editor sup.textsize-headline{font-size:1.4em}:host ::ng-deep .ql-container .ql-editor .textsize-h1:not(li){font-size:1.6em;line-height:1.2}:host ::ng-deep .ql-container .ql-editor li.textsize-h1:before,:host ::ng-deep .ql-container .ql-editor li.textsize-h1>.ql-ui:first-child:before{font-size:1.6em;line-height:1.2}:host ::ng-deep .ql-container .ql-editor sub.textsize-h1,:host ::ng-deep .ql-container .ql-editor sup.textsize-h1{font-size:1.12em}:host ::ng-deep .ql-container .ql-editor .textsize-h2:not(li){font-size:1.2em;line-height:1.2}:host ::ng-deep .ql-container .ql-editor li.textsize-h2:before,:host ::ng-deep .ql-container .ql-editor li.textsize-h2>.ql-ui:first-child:before{font-size:1.2em;line-height:1.2}:host ::ng-deep .ql-container .ql-editor sub.textsize-h2,:host ::ng-deep .ql-container .ql-editor sup.textsize-h2{font-size:.84em}:host ::ng-deep .ql-container .ql-editor .textsize-normal0:not(li){font-size:1.15em;line-height:1.2}:host ::ng-deep .ql-container .ql-editor li.textsize-normal0:before,:host ::ng-deep .ql-container .ql-editor li.textsize-normal0>.ql-ui:first-child:before{font-size:1.15em;line-height:1.2}:host ::ng-deep .ql-container .ql-editor sub.textsize-normal0,:host ::ng-deep .ql-container .ql-editor sup.textsize-normal0{font-size:.805em}:host ::ng-deep .ql-container .ql-editor .textsize-normal:not(li){font-size:1em;line-height:1.2}:host ::ng-deep .ql-container .ql-editor li.textsize-normal:before,:host ::ng-deep .ql-container .ql-editor li.textsize-normal>.ql-ui:first-child:before{font-size:1em;line-height:1.2}:host ::ng-deep .ql-container .ql-editor sub.textsize-normal,:host ::ng-deep .ql-container .ql-editor sup.textsize-normal{font-size:.7em}:host ::ng-deep .ql-container .ql-editor .textsize-small:not(li){font-size:.75em;line-height:1.2}:host ::ng-deep .ql-container .ql-editor li.textsize-small:before,:host ::ng-deep .ql-container .ql-editor li.textsize-small>.ql-ui:first-child:before{font-size:.75em;line-height:1.2}:host ::ng-deep .ql-container .ql-editor sub.textsize-small,:host ::ng-deep .ql-container .ql-editor sup.textsize-small{font-size:.525em}:host ::ng-deep .ql-container .ql-editor .textsize-smaller:not(li){font-size:.6em;line-height:1.2}:host ::ng-deep .ql-container .ql-editor li.textsize-smaller:before,:host ::ng-deep .ql-container .ql-editor li.textsize-smaller>.ql-ui:first-child:before{font-size:.6em;line-height:1.2}:host ::ng-deep .ql-container .ql-editor sub.textsize-smaller,:host ::ng-deep .ql-container .ql-editor sup.textsize-smaller{font-size:.42em}:host ::ng-deep .ql-container .ql-editor .textsize-smaller2:not(li){font-size:.512em;line-height:1.2}:host ::ng-deep .ql-container .ql-editor li.textsize-smaller2:before,:host ::ng-deep .ql-container .ql-editor li.textsize-smaller2>.ql-ui:first-child:before{font-size:.512em;line-height:1.2}:host ::ng-deep .ql-container .ql-editor sub.textsize-smaller2,:host ::ng-deep .ql-container .ql-editor sup.textsize-smaller2{font-size:.3584em}:host ::ng-deep .ql-container .ql-editor .textsize-smaller3:not(li){font-size:.402em;line-height:1.2}:host ::ng-deep .ql-container .ql-editor li.textsize-smaller3:before,:host ::ng-deep .ql-container .ql-editor li.textsize-smaller3>.ql-ui:first-child:before{font-size:.402em;line-height:1.2}:host ::ng-deep .ql-container .ql-editor sub.textsize-smaller3,:host ::ng-deep .ql-container .ql-editor sup.textsize-smaller3{font-size:.2814em}:host ::ng-deep .ql-container .ql-editor ul,:host ::ng-deep .ql-container .ql-editor ol{list-style-position:inside;padding-left:.75em}:host ::ng-deep .ql-container .ql-editor ul li,:host ::ng-deep .ql-container .ql-editor ol li{position:static;margin-bottom:.25em;padding-left:1.5em}:host ::ng-deep .ql-container .ql-editor ul li[data-list]>.ql-ui:first-child,:host ::ng-deep .ql-container .ql-editor ol li[data-list]>.ql-ui:first-child{position:static}:host ::ng-deep .ql-container .ql-editor ul li:last-child,:host ::ng-deep .ql-container .ql-editor ol li:last-child{margin-bottom:0}:host ::ng-deep .ql-container .ql-editor ul li.ql-indent-1,:host ::ng-deep .ql-container .ql-editor ol li.ql-indent-1{padding-left:3em}:host ::ng-deep .ql-container .ql-editor ul li.ql-indent-2,:host ::ng-deep .ql-container .ql-editor ol li.ql-indent-2{padding-left:4.5em}:host ::ng-deep .ql-container .ql-editor ul li.ql-indent-3,:host ::ng-deep .ql-container .ql-editor ol li.ql-indent-3{padding-left:6em}:host ::ng-deep .ql-container .ql-editor ul li.ql-indent-4,:host ::ng-deep .ql-container .ql-editor ol li.ql-indent-4{padding-left:7.5em}:host ::ng-deep .ql-container .ql-editor ul li.ql-indent-5,:host ::ng-deep .ql-container .ql-editor ol li.ql-indent-5{padding-left:9em}:host ::ng-deep .ql-container .ql-editor ul li.ql-indent-6,:host ::ng-deep .ql-container .ql-editor ol li.ql-indent-6{padding-left:10.5em}:host ::ng-deep .ql-container .ql-editor ul li.ql-indent-7,:host ::ng-deep .ql-container .ql-editor ol li.ql-indent-7{padding-left:12em}:host ::ng-deep .ql-container .ql-editor ul li.ql-indent-8,:host ::ng-deep .ql-container .ql-editor ol li.ql-indent-8{padding-left:13.5em}:host ::ng-deep .ql-container .ql-editor [class^=textcolor-],:host ::ng-deep .ql-container .ql-editor [class*=\" textcolor-\"]{color:#868e96}:host ::ng-deep .ql-container .ql-editor [class^=textbackground-],:host ::ng-deep .ql-container .ql-editor [class*=\" textbackground-\"]{background-color:#e9ecef}:host ::ng-deep .ql-container.textsize-base--headline3 .ql-editor{font-size:2.986em}:host ::ng-deep .ql-container.textsize-base--headline2 .ql-editor{font-size:2.488em}:host ::ng-deep .ql-container.textsize-base--headline .ql-editor{font-size:2em}:host ::ng-deep .ql-container.textsize-base--h1 .ql-editor{font-size:1.6em}:host ::ng-deep .ql-container.textsize-base--h2 .ql-editor{font-size:1.2em}:host ::ng-deep .ql-container.textsize-base--small .ql-editor{font-size:.75em}:host ::ng-deep .ql-container.textsize-base--small .ql-editor .textsize-headline3{font-size:3.9813333333em}:host ::ng-deep .ql-container.textsize-base--small .ql-editor .textsize-headline2{font-size:3.3173333333em}:host ::ng-deep .ql-container.textsize-base--small .ql-editor .textsize-headline{font-size:2.6666666667em}:host ::ng-deep .ql-container.textsize-base--small .ql-editor .textsize-h1{font-size:2.1333333333em}:host ::ng-deep .ql-container.textsize-base--small .ql-editor .textsize-h2{font-size:1.6em}:host ::ng-deep .ql-container.textsize-base--small .ql-editor .textsize-normal0{font-size:1.5333333333em}:host ::ng-deep .ql-container.textsize-base--small .ql-editor .textsize-normal{font-size:1.3333333333em}:host ::ng-deep .ql-container.textsize-base--small .ql-editor .textsize-small{font-size:1em}:host ::ng-deep .ql-container.textsize-base--small .ql-editor .textsize-smaller{font-size:.8em}:host ::ng-deep .ql-container.textsize-base--small .ql-editor .textsize-smaller2{font-size:.6826666667em}:host ::ng-deep .ql-container.textsize-base--small .ql-editor .textsize-smaller3{font-size:.536em}\n"] }]
        }], ctorParameters: () => [{ type: i0.ElementRef }, { type: i0.NgZone }, { type: i0.ChangeDetectorRef }, { type: WysiwygService }], propDecorators: { class: [{
                type: HostBinding,
                args: ['class']
            }], zIndex: [{
                type: HostBinding,
                args: ['style.z-index']
            }], editor: [{
                type: ViewChild,
                args: [QuillInputComponent, { static: true }]
            }], toolbar: [{
                type: ViewChild,
                args: [WysiwygToolbarComponent, { static: true }]
            }], placeholder: [{
                type: Input
            }], minHeight: [{
                type: Input
            }], textSizeBase: [{
                type: Input
            }], toolbarButtonsWrap: [{
                type: Input
            }], toolbarAlignment: [{
                type: Input
            }], defaultAlignment: [{
                type: Input
            }], textSizes: [{
                type: Input
            }], colorSet: [{
                type: Input
            }], alignments: [{
                type: Input
            }], styles: [{
                type: Input
            }], lists: [{
                type: Input
            }], keyBindings: [{
                type: Input
            }], history: [{
                type: Input
            }], legacyInputUI: [{
                type: Input
            }], isToolbarOpen: [{
                type: Input
            }], value: [{
                type: Input
            }], textSizeEnabled: [{
                type: Input
            }], alignmentEnabled: [{
                type: Input
            }], onChange: [{
                type: Output
            }], onFocus: [{
                type: Output
            }], onBlur: [{
                type: Output
            }] } });
const DIRECTIVES = [WysiwygComponent];
class WysiwygModule {
    static forRoot() {
        return {
            ngModule: WysiwygModule,
            providers: [WysiwygService],
        };
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.10", ngImport: i0, type: WysiwygModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.10", ngImport: i0, type: WysiwygModule, declarations: [WysiwygComponent], imports: [CommonModule, KappaModule, WysiwygToolbarModule, QuillInputModule], exports: [WysiwygComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.10", ngImport: i0, type: WysiwygModule, imports: [CommonModule, KappaModule, WysiwygToolbarModule, QuillInputModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.10", ngImport: i0, type: WysiwygModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule, KappaModule, WysiwygToolbarModule, QuillInputModule],
                    declarations: DIRECTIVES,
                    exports: DIRECTIVES,
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { ALIGNMENTS, BODY_1_TEXT_SIZE, BODY_2_TEXT_SIZE, BODY_3_TEXT_SIZE, BODY_4_TEXT_SIZE, BODY_5_TEXT_SIZE, BODY_6_TEXT_SIZE, BOLD_TEXT_STYLE, BULLET_LIST, CENTER_ALIGNMENT, HEADING_1_TEXT_SIZE, HEADING_2_TEXT_SIZE, HEADING_3_TEXT_SIZE, HEADING_4_TEXT_SIZE, HEADING_5_TEXT_SIZE, ITALIC_TEXT_STYLE, LEFT_ALIGNMENT, LISTS, ORDERED_LIST, QuillInputComponent, QuillInputModule, RIGHT_ALIGNMENT, STRIKE_TEXT_STYLE, STYLES, SUBSCRIPT_TEXT_STYLE, SUPERSCRIPT_TEXT_STYLE, TEXT_SIZES, UNDERLINE_TEXT_STYLE, WysiwygColorsSelectorComponent, WysiwygComponent, WysiwygModule, WysiwygService, WysiwygToolbarComponent, WysiwygToolbarModule };
